/**
 * violex-cart.js
 * Warenkorb-Seite (/pages/order): Positionen rendern, Versand anzeigen und den
 * Keychain-Upsell steuern. Der Gratis-Versand selbst wird an der echten Shopify-
 * Kasse durch die Function "Violex Free Shipping" gewährt (Keychain + weiterer
 * Artikel im Warenkorb). Diese Seite zeigt den Zustand nur an und bewirbt ihn.
 *
 * Fortschritts-Logik (Doku/ux-audit-shop-und-giveaway.md Abschnitt 3) teilt
 * sich mit dem Slide-in-Panel und der Header-Leiste die Funktion aus
 * assets/violex-cart-state.js (window.ViolexCartState), damit der
 * Gratis-Versand-Status überall gleich berechnet wird.
 *
 * Bugfixes (Doku §7 / Tiefenanalyse §2.2+§2.3, 28.08.2026):
 *  - changeQuantity() aktualisiert jetzt OPTIMISTISCH (sofort lokal, dann der
 *    Request) statt zwei Netzwerk-Roundtrips lang zu warten, und sperrt die
 *    betroffene Zeile waehrend der Request laeuft - verhindert gleichzeitig
 *    das alte Lost-Update-Problem bei schnellem Doppelklick.
 *  - Alle drei Cart-API-Aufrufe pruefen jetzt res.ok und rollen bei einem
 *    Fehler den lokalen Stand zurueck, statt ihn stillschweigend stehen zu
 *    lassen.
 *
 * Keychain-Upsell (Varianten laden, Swatches rendern, hinzufügen) kommt aus
 * assets/violex-keychain-upsell.js (window.ViolexKeychainUpsell) - geteilt
 * mit dem Slide-in-Panel (29.08.2026), muss VOR dieser Datei geladen sein.
 */

(function () {
  'use strict';

  let root = null;
  let cartData = null;
  let keychainHandle = 'vlx-nfc-keychain';
  let shippingCost = 6.9;
  let keychainVariants = [];
  let offerActive = false; // wird durch violex-offer.js / Admin-App-Angebot gesetzt
  const pendingKeys = new Set(); // Warenkorb-Zeilen mit einem laufenden Request

  document.addEventListener('DOMContentLoaded', init);

  async function init() {
    root = document.getElementById('violex-cart');
    if (!root) return;

    keychainHandle = root.dataset.keychainHandle || keychainHandle;
    shippingCost = parseFloat(root.dataset.shippingCost);
    if (isNaN(shippingCost)) shippingCost = 0;

    const raw = document.getElementById('vx-cart-data');
    if (!raw) return;
    cartData = JSON.parse(raw.textContent);

    if (cartData.itemCount === 0) {
      show('vx-empty');
      return;
    }

    show('vx-main');
    renderCartRows();
    updateSummary();
    renderBanner();
    await maybeRenderUpsell();

    // Echte Versandkosten nachziehen (siehe syncShippingRate). Bewusst
    // ohne await davor: die Seite steht schon, das ist nur eine Korrektur.
    syncShippingRate();

    // Angebots-UI erst freischalten, wenn ein aktives Angebot vorliegt (violex-offer.js).
    if (window.VIOLEX_OFFER_READY) {
      await applyOffer(window.VIOLEX_OFFER);
    } else {
      document.addEventListener(
        'violex:offer:ready',
        (e) => applyOffer(e.detail && e.detail.offer),
        { once: true },
      );
    }
  }

  async function applyOffer(offer) {
    offerActive = !!offer;
    updateSummary();
    renderBanner();
    await maybeRenderUpsell();
  }

  // ── Angebots-Fortschritt (geteilt mit Panel + Header-Leiste) ────────────────
  function offerProgress() {
    // window.ViolexCartState kommt aus assets/violex-cart-state.js (global,
    // vor dieser Datei geladen - siehe layout/theme.liquid). cartData hier ist
    // im internen Format (price bereits durch 100 geteilt, siehe mapLine), die
    // geteilte Funktion braucht aber die "handle"-Felder wie von /cart.js -
    // cartData.items hat productHandle statt handle, deshalb kurz gemappt.
    return window.ViolexCartState.computeOfferProgress(
      { items: cartData.items.map((i) => ({ handle: i.productHandle })) },
      offerActive,
      keychainHandle,
    );
  }
  function hasKeychain() {
    return cartData.items.some((i) => i.productHandle === keychainHandle);
  }
  function hasOther() {
    return cartData.items.some((i) => i.productHandle !== keychainHandle);
  }
  // Delegiert an offerProgress() statt eigener Kopie: freeShippingActive
  // entscheidet hier die tatsaechliche Preisberechnung (updateSummary) - war
  // vorher NUR an offerActive+Komposition gebunden, unabhaengig vom echten
  // Trigger-Metafeld, das die Kasse prueft. Eine zweite Kopie dieser
  // Bedingung waere sonst wieder genau das §2.1-Problem, nur eine Ebene
  // tiefer.
  function freeShippingActive() {
    return offerProgress().freeShippingActive;
  }

  // ── Positionen rendern ───────────────────────────────────────────────────────
  function renderCartRows() {
    const tbody = document.getElementById('vx-cart-rows');
    if (!tbody) return;

    tbody.innerHTML = cartData.items
      .map((item) => {
        const lineTotal = money(item.price * item.quantity);
        const isPending = pendingKeys.has(item.key);
        const qtyCell = `<div class="vxc__qty-controls">
             <button class="vxc__qty-btn" data-key="${escAttr(item.key)}" data-delta="-1" aria-label="Weniger"${isPending ? ' disabled' : ''}>−</button>
             <span class="vxc__qty-num">${item.quantity}</span>
             <button class="vxc__qty-btn" data-key="${escAttr(item.key)}" data-delta="1" aria-label="Mehr"${isPending ? ' disabled' : ''}>+</button>
           </div>`;
        const removeCell = `<button class="vxc__remove-btn" data-key="${escAttr(item.key)}" aria-label="Entfernen"${isPending ? ' disabled' : ''}>×</button>`;
        const variant =
          item.variantTitle && item.variantTitle !== 'Default Title'
            ? escHtml(item.variantTitle)
            : '—';

        // Bild-Zelle (29.08.2026): item.image kommt aus dem Snapshot in
        // sections/cart-with-offers.liquid und ist null, wenn das Produkt
        // kein Bild hat - dann bleibt die Zelle leer statt ein kaputtes
        // <img> zu rendern. alt="" weil rein dekorativ, der Produktname
        // steht in der Zelle daneben.
        const imgCell = item.image
          ? `<img src="${escAttr(item.image)}" alt="" width="64" height="64" loading="lazy">`
          : '';

        return `<tr>
        <td class="vxc__table-img">${imgCell}</td>
        <td class="vxc__table-product">${escHtml(item.productTitle)}</td>
        <td class="vxc__table-variant">${variant}</td>
        <td class="vxc__table-qty">${qtyCell}</td>
        <td class="vxc__table-price">${lineTotal}</td>
        <td class="vxc__table-remove">${removeCell}</td>
      </tr>`;
      })
      .join('');

    tbody.querySelectorAll('.vxc__qty-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const delta = parseInt(btn.dataset.delta, 10);
        const item = cartData.items.find((i) => i.key === key);
        if (!item) return;
        await changeQuantity(key, Math.max(0, item.quantity + delta));
      });
    });

    tbody.querySelectorAll('.vxc__remove-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        await changeQuantity(btn.dataset.key, 0);
      });
    });
  }

  // ── Menge ändern (optimistisch, mit Rollback) ────────────────────────────────
  async function changeQuantity(key, newQty) {
    // Laeuft fuer diese Zeile schon ein Request, keinen zweiten anstossen -
    // genau das war vorher die Lost-Update-Quelle (zwei ueberlappende
    // Requests, beide auf dem gleichen veralteten Stand berechnet).
    if (pendingKeys.has(key)) return;

    const idx = cartData.items.findIndex((i) => i.key === key);
    if (idx === -1) return;
    const previousItem = cartData.items[idx];
    const removing = newQty === 0;

    hideCartError();
    pendingKeys.add(key);

    // Sofort lokal anwenden und neu rendern, BEVOR die Antwort da ist - fühlt
    // sich damit augenblicklich an statt nach zwei Roundtrips.
    if (removing) {
      cartData.items.splice(idx, 1);
    } else {
      cartData.items[idx] = Object.assign({}, previousItem, { quantity: newQty });
    }
    cartData.itemCount = cartData.items.reduce((n, i) => n + i.quantity, 0);
    renderCartRows();
    updateSummary();

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty }),
      });
      const data = await safeJson(res);
      if (!res.ok || !data) {
        throw new Error((data && data.description) || 'Menge konnte nicht geändert werden.');
      }

      cartData.items = data.items.map(mapLine);
      cartData.itemCount = data.item_count;
      cartData.totalPrice = data.total_price;

      if (cartData.itemCount === 0) {
        document.getElementById('vx-main').style.display = 'none';
        show('vx-empty');
      } else {
        renderCartRows();
        updateSummary();
        renderBanner();
        await maybeRenderUpsell();
      }
      notifyCart();
    } catch (err) {
      // Rollback: alten Stand wiederherstellen statt den Fehler stillschweigend
      // stehen zu lassen (das war vorher exakt der Fall - res.ok wurde gar
      // nicht geprüft).
      if (removing) {
        cartData.items.splice(idx, 0, previousItem);
      } else {
        cartData.items[idx] = previousItem;
      }
      cartData.itemCount = cartData.items.reduce((n, i) => n + i.quantity, 0);
      hide('vx-empty');
      document.getElementById('vx-main').style.display = '';
      renderCartRows();
      updateSummary();
      showCartError(err.message || 'Menge konnte nicht geändert werden. Bitte erneut versuchen.');
      console.error('changeQuantity failed', err);
    } finally {
      // Beide Zweige oben rendern die Zeilen bereits neu (Erfolg: im
      // if/else, ausser der Warenkorb wurde leer und die Tabelle ist ohnehin
      // ausgeblendet; Fehler: im catch) - hier nur noch das Pending-Flag
      // raeumen, kein weiteres Render noetig.
      pendingKeys.delete(key);
    }
  }

  // i.final_price statt i.price: final_price ist der Preis NACH Rabatten
  // (z.B. einem aktiven Angebot), i.price immer der volle, unrabattierte
  // Ausgangspreis. cart-with-offers.liquid rendert die Zeile serverseitig
  // schon mit item.final_line_price - i.price hier haette das beim ersten
  // Rendern durch renderCartRows() sofort wieder auf den vollen Preis
  // zurueckgesetzt (Bugreport 29.08.2026: Warenkorb-Seite zeigte den vollen
  // Preis, das Slide-in-Panel - das bereits final_line_price/
  // items_subtotal_price nutzt - den rabattierten).
  function mapLine(i) {
    return {
      key: i.key,
      productHandle: i.handle,
      productTitle: i.product_title,
      variantTitle: i.variant_title,
      quantity: i.quantity,
      price: i.final_price / 100,
      properties: i.properties,
    };
  }

  // ── Summe + Versand ──────────────────────────────────────────────────────────
  function updateSummary() {
    const subtotal = cartData.items.reduce((s, i) => s + i.price * i.quantity, 0);
    const free = freeShippingActive();
    const shipping = cartData.itemCount > 0 && !free ? shippingCost : 0;

    setText('vx-subtotal', money(subtotal));

    // "ab" statt einer nackten Zahl (Doku/ux-audit-shop-und-giveaway.md §4):
    // der Betrag ist vor der Adresseingabe immer eine Schaetzung, nie der
    // Kasse-Endbetrag - "ab" sagt das, ohne dass die Kasse widersprechen kann.
    // Muss mit dem serverseitig vorgerenderten Text (cart-with-offers.liquid)
    // uebereinstimmen, sonst springt der Text beim JS-Uebernehmen sichtbar um.
    const shipLabel = document.getElementById('vx-shipping-label');
    if (shipLabel) shipLabel.textContent = free ? 'Gratis' : 'ab ' + money(shippingCost);
    const shipRow = document.getElementById('vx-shipping-row');
    if (shipRow) shipRow.style.color = free ? 'rgba(134,239,172,0.9)' : '';

    setText('vx-total', money(subtotal + shipping));
  }

  // ── Angebots-Fortschrittsbalken (Doku §3: Fortschritt statt Hinweis) ────────
  function renderBanner() {
    const banner = document.getElementById('vx-offer-banner');
    if (!banner) return;

    const progress = offerProgress();
    if (!progress.active) {
      banner.style.display = 'none';
      return;
    }

    banner.className = 'vxc__offer-banner' + (progress.freeShippingActive ? ' is-complete' : '');
    banner.innerHTML = `
      <p class="vxc__offer-banner__label">${escHtml(progress.label)}</p>
      <div class="vxc__offer-banner__track">
        <div class="vxc__offer-banner__fill" style="width:${progress.percent}%"></div>
      </div>`;
    banner.style.display = '';
  }

  // ── Angebots-Upsell ──────────────────────────────────────────────────────────
  // Die Karte zeigt jetzt BEIDE Richtungen. Vorher griff sie nur bei "Polo
  // drin, kein Keychain" - lag nur ein Keychain im Warenkorb, blieb der Hinweis
  // komplett aus, obwohl derselbe eine Artikel zum Gratis-Versand gefehlt hat.
  // Beide Faelle nutzen dieselbe Karte, nur Beschriftung und Inhalt wechseln.
  const POLO_PAGES = [
    { label: 'Day Edition', url: '/pages/day-edition' },
    { label: 'Night Edition', url: '/pages/night-edition' },
  ];

  async function maybeRenderUpsell() {
    const wrapper = document.getElementById('vx-paid-keychain');
    if (!wrapper) return;

    const labelEl = wrapper.querySelector('.vxc__keychain-label');
    const descEl = wrapper.querySelector('.vxc__keychain-desc');
    const swatchesEl = document.getElementById('vx-paid-keychain-swatches');

    const needsKeychain = hasOther() && !hasKeychain();
    const needsOther = hasKeychain() && !hasOther();

    if (!offerActive || (!needsKeychain && !needsOther)) {
      wrapper.style.display = 'none';
      return;
    }
    wrapper.style.display = '';

    // Umgekehrter Fall: nur ein Keychain im Warenkorb. Hier gibt es nichts per
    // Swatch hinzuzufuegen (ein Polo braucht eine Groessenwahl), deshalb
    // fuehren zwei Links auf die Produktseiten statt eines Direkt-Buttons.
    if (needsOther) {
      hide('vx-paid-keychain-loading');
      if (labelEl) labelEl.textContent = '✦ Polo hinzufügen — Gratis-Versand';
      if (descEl) {
        descEl.textContent =
          `Dein Keychain schaltet den Gratis-Versand frei, sobald ein weiterer ` +
          `Artikel dazukommt - du sparst ${money(shippingCost)}.`;
      }
      if (swatchesEl) {
        swatchesEl.innerHTML = POLO_PAGES.map(
          (polo) =>
            `<a class="vxc-btn vxc-btn--outline" href="${polo.url}">${polo.label} ansehen →</a>`,
        ).join(' ');
      }
      return;
    }

    if (labelEl) labelEl.textContent = '✦ Keychain hinzufügen — Gratis-Versand';

    if (keychainVariants.length === 0) {
      show('vx-paid-keychain-loading');
      keychainVariants = await window.ViolexKeychainUpsell.fetchVariants(keychainHandle);
      hide('vx-paid-keychain-loading');
      // Der Banner wurde vor dem Laden gerendert und kannte den Preis noch
      // nicht - jetzt nachziehen, sonst fehlt dort dauerhaft die Zahl.
      renderBanner();
    }

    // Effektiver Preis statt Bruttopreis (Doku §3.1): "Ergänze einen Keychain
    // für CHF 9.90" klingt nach einem Minusgeschäft gegen die gesparten
    // CHF 6.90 Versand - "effektiv CHF 3.00" sagt dasselbe, ist staerker, und
    // stimmt (Keychain-Preis minus gesparten Versand).
    const kp = window.ViolexKeychainUpsell.firstAvailablePrice(keychainVariants);
    if (descEl) {
      if (kp !== null) {
        const effective = Math.max(0, kp - shippingCost);
        descEl.textContent = `Keychain für effektiv ${money(effective)} (spart dir ${money(shippingCost)} Versand).`;
      } else {
        descEl.textContent = `Ergänze einen Keychain und dein Versand (${money(shippingCost)}) wird gratis.`;
      }
    }
    renderSwatches();
  }

  // Swatch-Rendering + Add-Flow kommen aus assets/violex-keychain-upsell.js -
  // geteilt mit dem Slide-in-Panel (violex-cart-drawer.js), damit dieselbe
  // Logik nicht zweimal gepflegt wird.
  function renderSwatches() {
    const swatchesEl = document.getElementById('vx-paid-keychain-swatches');
    if (!swatchesEl) return;
    window.ViolexKeychainUpsell.renderSwatches(swatchesEl, keychainVariants, '/pages/keychain', onAddKeychain);
  }

  async function onAddKeychain(variantId, swatchEl) {
    hideCartError();
    const group = swatchEl.closest('.vx-swatches');
    group.querySelectorAll('.vx-swatch').forEach((b) => (b.disabled = true));
    swatchEl.classList.add('active');

    try {
      await window.ViolexKeychainUpsell.addVariant(variantId);
      await refreshCart();

      renderCartRows();
      updateSummary();
      renderBanner();
      await maybeRenderUpsell();
      notifyCart();
    } catch (err) {
      showCartError(err.message || 'Keychain konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
      console.error('onAddKeychain failed', err);
      group.querySelectorAll('.vx-swatch').forEach((b) => (b.disabled = false));
      swatchEl.classList.remove('active');
    }
  }

  async function refreshCart() {
    const updated = await fetchCart();
    cartData.items = updated.items.map(mapLine);
    cartData.itemCount = updated.item_count;
    cartData.totalPrice = updated.total_price;
    cartData.totalWeight = updated.total_weight;
    await syncShippingRate();
  }

  /**
   * Holt die ECHTEN Versandkosten aus den Shopify-Versandzonen und ersetzt
   * damit den Schaetzwert aus den Section-Einstellungen (29.08.2026, Wunsch
   * Tomas). Details und Einschraenkungen: siehe fetchDomesticShippingRate in
   * assets/violex-cart-state.js.
   *
   * Bewusst NACHTRAEGLICH statt blockierend: die Seite rendert sofort mit
   * dem Schaetzwert und korrigiert sich, sobald die Antwort da ist. Kommt
   * keine (Netzwerkfehler, leerer Warenkorb), bleibt der bisherige Wert
   * stehen - nie ein leeres oder falsches Feld.
   *
   * shippingCost fliesst ausser in die Summe auch in die Upsell-Texte
   * ("spart dir CHF X Versand"), deshalb wird maybeRenderUpsell mit
   * aktualisiert - sonst stuende dort weiter der alte Betrag.
   */
  async function syncShippingRate() {
    const state = window.ViolexCartState;
    if (!state || !state.fetchDomesticShippingRate || !cartData) return;

    const rate = await state.fetchDomesticShippingRate({
      item_count: cartData.itemCount,
      total_weight: cartData.totalWeight,
    });
    if (rate === null || rate === shippingCost) return;

    shippingCost = rate;
    updateSummary();
    await maybeRenderUpsell();
  }

  // ── Cart-API-Helfer ──────────────────────────────────────────────────────────
  async function fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('Warenkorb konnte nicht geladen werden.');
    return res.json();
  }

  function notifyCart() {
    document.dispatchEvent(
      new CustomEvent('violex:cart:updated', { detail: { count: cartData.itemCount } }),
    );
  }

  // ── Helfer ───────────────────────────────────────────────────────────────────
  function show(id) {
    const el = document.getElementById(id);
    if (el) el.style.display = '';
  }
  function hide(id) {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  }
  function setText(id, text) {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  }
  function showCartError(message) {
    const el = document.getElementById('vx-cart-error');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }
  function hideCartError() {
    const el = document.getElementById('vx-cart-error');
    if (el) el.hidden = true;
  }
  /** res.text() + eigenes JSON.parse statt res.json(): kommt keine JSON-Antwort
   *  zurück (Shopify-Fehlerseite, Wartungsmodus, Proxy), wirft res.json() eine
   *  Parser-Meldung wie "Unexpected token '<'", die dann woertlich vor dem
   *  Kunden stuende. Gleiches Muster wie in violex-edition.js. */
  async function safeJson(res) {
    try {
      const raw = await res.text();
      return JSON.parse(raw);
    } catch (_e) {
      return null;
    }
  }
  function escHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
  function escAttr(str) {
    return String(str || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function money(value) {
    return 'CHF ' + Number(value || 0).toFixed(2);
  }
})();
